/**
 * Study Domain Types
 *
 * Mirrors backend domain types for DICOM medical imaging studies
 */

/**
 * Study workflow status
 */
export type StudyStatus = 'RECEIVED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'

/**
 * Study status labels for UI display
 * Semantic labels based on radiology workflow actions
 */
export const STUDY_STATUS_LABELS: Record<StudyStatus, string> = {
  RECEIVED: 'Por Leer',
  IN_PROGRESS: 'En Lectura',
  COMPLETED: 'Leído',
  CANCELLED: '', // No usar - legacy
}

/**
 * Study status colors for badges
 */
export const STUDY_STATUS_COLORS: Record<StudyStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  RECEIVED: 'default',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
}

/**
 * Main Study type matching backend response
 */
export interface Study {
  id: string
  // DICOM identifiers
  studyInstanceUID: string
  accessionNumber: string | null
  // Patient reference
  patientId: string
  // Study metadata
  modality: string
  studyDate: string
  studyTime: string | null
  studyDescription: string | null
  // Physician information
  referringPhysician: string | null
  performingPhysician: string | null
  // Study details
  bodyPartExamined: string | null
  numberOfSeries: number
  numberOfInstances: number
  // Institution
  institutionName: string | null
  institutionAddress: string | null
  // Storage
  storagePath: string
  storageSizeBytes: number | null
  // Workflow
  status: StudyStatus
  reportId: string | null
  // Audit
  uploadedBy: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Study with patient information for list views
 */
export interface StudyWithPatient extends Study {
  patientName: string
  patientDocumentType: string
  patientDocumentNumber: string
}

/**
 * Patient data for match/create when uploading studies
 */
export interface PatientCreateData {
  documentNumber: string
  documentType?: string
  firstName?: string
  lastName?: string
  middleName?: string
  secondLastName?: string
  birthDate?: string
  sex?: 'M' | 'F' | 'O'
}

/**
 * Single file input for batch upload
 */
export interface BatchFileInput {
  fileData: string // Base64 encoded
  fileName: string
  sopInstanceUID?: string
  seriesInstanceUID?: string
  instanceNumber?: number
  contentType?: string
}

/**
 * Data for creating a new study
 */
export interface CreateStudyData {
  studyInstanceUID: string
  accessionNumber?: string
  // Patient option A: existing patient ID
  patientId?: string
  // Patient option B: data for match/create
  patientData?: PatientCreateData
  modality: string
  studyDate: string
  studyTime?: string
  studyDescription?: string
  referringPhysician?: string
  performingPhysician?: string
  bodyPartExamined?: string
  numberOfSeries?: number
  numberOfInstances?: number
  institutionName?: string
  institutionAddress?: string
  // For single file upload
  fileData?: string // Base64 encoded
  fileName?: string
  contentType?: string
  // For batch upload (multiple files)
  files?: BatchFileInput[]
  // Attachments (mandatory for single upload)
  attachments?: Array<{
    attachmentType: string
    fileName: string
    fileData: string
    mimeType?: string
    description?: string
  }>
}

/**
 * Data for updating a study
 */
export interface UpdateStudyData {
  studyDescription?: string
  referringPhysician?: string
  performingPhysician?: string
  bodyPartExamined?: string
  numberOfSeries?: number
  numberOfInstances?: number
  status?: StudyStatus
  reportId?: string
}

/**
 * Filters for searching studies
 */
export interface StudySearchFilters {
  patientId?: string
  modality?: string
  status?: StudyStatus
  studyDateFrom?: string
  studyDateTo?: string
  accessionNumber?: string
  referringPhysician?: string
  institutionName?: string
  limit?: number
  offset?: number
}

/**
 * Paginated response for study list
 */
export interface StudyListResponse {
  studies: StudyWithPatient[]
  total: number
  limit: number
  offset: number
}

/**
 * Body parts commonly examined in DICOM studies
 */
export const BODY_PARTS = {
  HEAD: 'HEAD',
  NECK: 'NECK',
  CHEST: 'CHEST',
  ABDOMEN: 'ABDOMEN',
  PELVIS: 'PELVIS',
  SPINE: 'SPINE',
  EXTREMITY: 'EXTREMITY',
  HAND: 'HAND',
  FOOT: 'FOOT',
  KNEE: 'KNEE',
  SHOULDER: 'SHOULDER',
  HIP: 'HIP',
  BREAST: 'BREAST',
  HEART: 'HEART',
  WHOLE_BODY: 'WHOLE BODY',
} as const

export type BodyPart = (typeof BODY_PARTS)[keyof typeof BODY_PARTS]

export const BODY_PART_LABELS: Record<BodyPart, string> = {
  HEAD: 'Cabeza',
  NECK: 'Cuello',
  CHEST: 'Tórax',
  ABDOMEN: 'Abdomen',
  PELVIS: 'Pelvis',
  SPINE: 'Columna',
  EXTREMITY: 'Extremidad',
  HAND: 'Mano',
  FOOT: 'Pie',
  KNEE: 'Rodilla',
  SHOULDER: 'Hombro',
  HIP: 'Cadera',
  BREAST: 'Mama',
  HEART: 'Corazón',
  'WHOLE BODY': 'Cuerpo Completo',
}

/**
 * Amendment data for amending a returned study
 */
export interface AmendStudyData {
  amendmentNote: string
  metadata?: {
    studyDescription?: string
    referringPhysician?: string
    performingPhysician?: string
    bodyPartExamined?: string
  }
  deleteInstanceIds?: string[]
  newInstances?: Array<{
    fileData: string
    fileName: string
    sopInstanceUID?: string
    seriesInstanceUID?: string
    instanceNumber?: number
  }>
  deleteAttachmentIds?: string[]
  newAttachments?: Array<{
    attachmentType: string
    fileName: string
    fileData: string
    mimeType?: string
    description?: string
  }>
}

/**
 * Study amendment record
 */
export interface StudyAmendment {
  id: string
  studyId: string
  amendedBy: string
  amendedAt: string
  amendmentNote: string
  metadataChanges: Record<string, { old: string | null; new: string | null }> | null
  instancesDeleted: number
  instancesAdded: number
  attachmentsDeleted: number
  attachmentsAdded: number
  returnReason: string | null
  reassignedTo: string | null
}

/**
 * Instance info for amend page
 */
export interface AmendInstanceInfo {
  id: string
  sopInstanceUID: string
  seriesDescription: string | null
  instanceNumber: number | null
  thumbnailUrl: string | null
}

/**
 * Attachment info for amend page
 */
export interface AmendAttachmentInfo {
  id: string
  attachmentType: string
  fileName: string
  fileSize: number | null
  mimeType: string | null
  description: string | null
}

/**
 * Amend info response
 */
export interface StudyAmendInfo {
  study: Study
  worklistItem: {
    id: string
    status: string
    returnReason: string | null
    returnedAt: string | null
    returnedBy: string | null
  }
  instances: AmendInstanceInfo[]
  attachments: AmendAttachmentInfo[]
  amendmentHistory: StudyAmendment[]
  canAmend: boolean
}
